import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { catalogApi } from '@/lib/api';
import { ProductCard, ProductCardSkeleton } from '@/components/ProductCard';
import { CategoryGlyph } from '@/components/CategoryGlyph';
import { Swatch } from '@/components/Swatch';
import { useLocale, useLocalePath } from '@/hooks/useLocalePath';
import { Seo } from '@/components/Seo';
import type { JSX } from 'react';

/**
 * Category sizes run 760 down to 9, so the grid is weighted rather than uniform — eight
 * equal tiles over this tree would look broken.
 */
const SPAN: Record<string, string> = {
  xl: 'md:col-span-3 md:row-span-2 aspect-[4/3] md:aspect-auto',
  lg: 'md:col-span-3 aspect-[16/9]',
  md: 'md:col-span-2 aspect-[4/3]',
  sm: 'md:col-span-2 aspect-[3/2]',
};

export function HomePage(): JSX.Element {
  const { t } = useTranslation();
  const locale = useLocale();
  const path = useLocalePath();

  const { data, isLoading } = useQuery({
    queryKey: ['home', locale],
    queryFn: () => catalogApi.home(locale),
    staleTime: 60_000,
  });

  return (
    <>
      <Seo title={t('common.brand')} description={t('home.heroSubtitle')} path="/" />

      <section className="rz-shell pt-10 pb-16 md:pt-20 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl"
        >
          <p className="rz-eyebrow mb-4">{t('common.tagline')}</p>
          <h1 className="text-2xl md:text-3xl">{t('home.heroTitle')}</h1>
          <p className="mt-5 max-w-xl text-base text-[var(--color-ink-soft)] leading-relaxed">
            {t('home.heroSubtitle')}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={path('/kategoria/narozniki')} className="rz-button">{t('home.heroCta')}</Link>
            <Link to={path('/tkaniny')} className="rz-button rz-button--ghost">{t('home.heroCtaSecondary')}</Link>
          </div>
        </motion.div>
      </section>

      {/* Weighted category grid */}
      <section className="rz-shell pb-20" aria-labelledby="home-categories">
        <h2 id="home-categories" className="rz-eyebrow mb-5">{t('home.byCategory')}</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          {isLoading
            ? Array.from({ length: 5 }, (_, i) => <div key={i} className="rz-skeleton md:col-span-2 aspect-[4/3]" />)
            : (data?.categories ?? []).map((category, index) => (
                <motion.div
                  key={category.slug}
                  // Transform only — see ProductCard: a reveal must never gate visibility.
                  // The stagger is capped so the last tile is not a second behind the
                  // first; past about five steps a stagger stops reading as choreography
                  // and starts reading as lag.
                  initial={{ y: 14 }}
                  whileInView={{ y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.55, delay: Math.min(index, 5) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className={SPAN[category.weight ?? 'md']}
                >
                  <Link
                    to={path(`/kategoria/${category.slug}`)}
                    className="group relative flex h-full min-h-[210px] flex-col justify-end overflow-hidden bg-[var(--color-ground-lift)] p-6 ring-1 ring-[var(--color-line)] transition-[box-shadow] duration-[var(--dur-panel)] ease-[var(--ease-spring)] hover:ring-[var(--color-brand)]/30"
                  >
                    {category.image?.card ? (
                      <>
                        {/* `cover` here, against `contain` everywhere else: a tile is a
                            crop by definition, and the product is shown whole one click
                            later. Which photograph gets chosen is the interesting part —
                            see CategoryService.withImages. */}
                        <img
                          src={category.image.card}
                          alt=""
                          aria-hidden="true"
                          loading={index < 3 ? 'eager' : 'lazy'}
                          decoding="async"
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[var(--ease-spring)] group-hover:scale-[1.05] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                        />
                        {/* Scrim and wash in the site's own colours rather than neutral
                            black. Supplier photography arrives in whatever temperature the
                            supplier's studio had, and a shared cast is what makes seven
                            unrelated shots look like one page. Kept at 6%: past about ten
                            the light rooms turn murky, which costs more than the cohesion
                            is worth. The gradient holds most of its weight below the
                            midpoint so it darkens the label, not the furniture. */}
                        <div
                          aria-hidden="true"
                          className="absolute inset-0 bg-[var(--color-uph-deep)]/[0.06] mix-blend-multiply"
                        />
                        <div
                          aria-hidden="true"
                          className="absolute inset-0 bg-gradient-to-t from-[var(--color-uph-deep)]/88 via-[var(--color-uph-deep)]/22 via-45% to-transparent transition-opacity duration-[var(--dur-panel)] group-hover:from-[var(--color-uph-deep)]/94"
                        />
                      </>
                    ) : (
                      /* No usable photograph: the drawn glyph rather than an empty box. */
                      <div className="absolute inset-0 flex items-end justify-center pb-14">
                        <CategoryGlyph slug={category.slug} className="h-[62%] w-auto max-w-[70%]" />
                      </div>
                    )}
                    <h3
                      className={`relative shrink-0 font-display text-lg transition-transform duration-[var(--dur-panel)] ease-[var(--ease-spring)] group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0 ${
                        category.image?.card ? 'text-white' : 'text-[var(--color-ink)]'
                      }`}
                    >
                      {category.name}
                    </h3>
                    <p
                      className={`relative mt-1 shrink-0 text-xs transition-transform duration-[var(--dur-panel)] delay-[40ms] ease-[var(--ease-spring)] group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0 ${
                        category.image?.card ? 'text-white/80' : 'text-[var(--color-ink-soft)]'
                      }`}
                    >
                      {t('home.productsCount', { count: category.productCount })}
                    </p>
                    <span
                      aria-hidden="true"
                      className={`relative mt-3 h-px w-8 origin-left scale-x-0 transition-transform duration-[520ms] ease-[var(--ease-spring)] group-hover:scale-x-100 motion-reduce:transition-none ${
                        category.image?.card ? 'bg-white/70' : 'bg-[var(--color-brand)]'
                      }`}
                    />
                  </Link>
                </motion.div>
              ))}
        </div>
      </section>

      {/* The signature element: the Material Wall */}
      <section className="bg-[var(--color-ground-deep)] py-20" aria-labelledby="home-fabrics">
        <div className="rz-shell">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div className="max-w-lg">
              <p className="rz-eyebrow mb-3">{t('home.fabricLibraryTitle')}</p>
              <h2 id="home-fabrics" className="text-xl md:text-2xl">{t('fabrics.title')}</h2>
              <p className="mt-3 text-sm text-[var(--color-ink-soft)] leading-relaxed">{t('home.fabricLibraryLead')}</p>
            </div>
            <Link to={path('/tkaniny')} className="rz-button rz-button--ghost">{t('common.showMore')}</Link>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {(data?.fabricFamilies ?? []).slice(0, 10).map((family, index) => (
              <motion.div
                key={family.slug}
                initial={{ y: 12 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: Math.min(index, 6) * 0.04 }}
              >
                <Link
                  to={path(`/tkaniny?rodzina=${family.slug}`)}
                  className="block bg-[var(--color-ground-lift)] p-4 transition-transform duration-300 hover:-translate-y-1"
                >
                  <p className="font-semibold text-sm">{family.label}</p>
                  <p className="text-2xs text-[var(--color-muted)] mb-3">
                    {t('home.productsCount', { count: family.productCount })}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {family.colors.slice(0, 8).map((color) => (
                      <Swatch key={color.slug} hex={color.hex} label={color.label} size={18} />
                    ))}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/*
        Featured, merchandised. The api has returned a `featured` set all along and the
        homepage rendered it nowhere, so every product on the page carried identical weight
        and nothing told a customer what the shop was leading with.

        One product takes half the section at 4:5; three more sit beside it at a smaller
        size. The asymmetry *is* the message.
      */}
      {(data?.featured?.length ?? 0) > 0 && (
        <section className="rz-shell py-20" aria-labelledby="home-featured">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="rz-eyebrow mb-2">{t('home.featuredEyebrow')}</p>
              <h2 id="home-featured" className="text-xl md:text-2xl">{t('home.featured')}</h2>
            </div>
            <Link to={path('/kategoria/narozniki')} className="rz-button rz-button--ghost">
              {t('common.showMore')}
            </Link>
          </div>

          <div className="grid gap-x-6 gap-y-12 lg:grid-cols-2">
            {data?.featured?.[0] && (
              <ProductCard product={data.featured[0]} index={0} size="feature" />
            )}
            <div className="grid grid-cols-2 gap-x-4 gap-y-10 self-start">
              {(data?.featured ?? []).slice(1, 5).map((product, i) => (
                <ProductCard key={product.id} product={product} index={i + 1} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Secondary by design: denser grid, smaller frame, less air. */}
      <section className="bg-[var(--color-ground-deep)] py-16" aria-labelledby="home-new">
        <div className="rz-shell">
          <h2 id="home-new" className="rz-eyebrow mb-6">{t('home.newArrivals')}</h2>
          <div className="grid grid-cols-2 gap-x-3 gap-y-8 sm:grid-cols-3 lg:grid-cols-6">
            {isLoading
              ? Array.from({ length: 6 }, (_, i) => <ProductCardSkeleton key={i} />)
              : (data?.newArrivals ?? []).slice(0, 6).map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} size="compact" />
                ))}
          </div>
        </div>
      </section>
    </>
  );
}
